import React, { useState, Suspense, lazy } from "react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Star, DollarSign, Play, CheckCircle, Clock, Send, X, Check, CreditCard } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { UserStatusIndicator } from "@/components/UserStatusIndicator";
import { UserAvatar } from "@/components/UserAvatar";
const PaymentModal = lazy(() => import("@/components/PaymentModal"));

interface ConversationData {
  id: string;
  participant1_id: string;
  participant2_id: string;
  service_id?: string;
  service_started?: boolean;
  service_completed?: boolean;
  final_price?: number | null;
  price_agreed_by_non_posting_party?: boolean;
  price_confirmed_by_provider?: boolean;
  latest_proposal_price?: number | null;
  latest_proposal_pricing_type?: string | null;
  latest_proposal_by?: string | null;
  payment_completed?: boolean;
  services?: { title: string; price: number; pricing_type: string; user_id: string } | null;
}

interface Recipient {
  id: string;
  name: string;
  avatarInitials: string;
  avatarUrl?: string;
}

interface ServiceHeaderProps {
  recipient: Recipient;
  conversation: ConversationData | null;
  isProvider: boolean;
  isClient: boolean;
  onUpdate: () => void;
  onServiceCompleted?: () => void;
}

export default function ServiceHeader({ 
  recipient,
  conversation,
  isProvider,
  isClient,
  onUpdate,
  onServiceCompleted
}: ServiceHeaderProps) {
  const { user } = useAuth();
  const [isProposePrice, setIsProposePrice] = useState(false);
  const [proposedPrice, setProposedPrice] = useState("");
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // In conversations, roles are consistent:
  // - participant2 = provider
  // - participant1 = client
  const providerId = conversation?.participant2_id;
  const clientId = conversation?.participant1_id;
  const isCurrentUserProvider = !!user?.id && !!providerId && user.id === providerId;
  const isCurrentUserClient = !!user?.id && !!clientId && user.id === clientId;

  const getStatus = () => {
    if (!conversation?.service_id) return null;
    if (conversation.service_completed) return 'completed';
    if (conversation.service_started) return 'in_progress';
    return 'proposal';
  };

  const getStatusBadge = (status: string | null) => {
    if (!status) return null;
    
    const variants = {
      proposal: { icon: Clock, color: "text-orange-600", label: "PROPOSAL" },
      in_progress: { icon: Play, color: "text-blue-600", label: "IN PROGRESS" },
      completed: { icon: CheckCircle, color: "text-green-600", label: "COMPLETED" }
    };

    const config = variants[status as keyof typeof variants];
    const Icon = config.icon;

    return (
      <Badge variant="secondary" className="flex items-center gap-1">
        <Icon className={`h-3 w-3 ${config.color}`} />
        {config.label}
      </Badge>
    );
  };

  const getCurrentPrice = () => {
    if (conversation?.final_price) return conversation.final_price;
    if (conversation?.latest_proposal_price) return conversation.latest_proposal_price;
    return conversation?.services?.price || 0;
  };

  const getPricingTypeDisplay = () => {
    // Everything is per job, no need to display
    return '';
  };

  const sendPriceProposal = async () => {
    if (!proposedPrice.trim() || !conversation) return;

    const price = parseFloat(proposedPrice);
    if (isNaN(price) || price <= 0) {
      toast.error("Please enter a valid price");
      return;
    }

    setIsSubmitting(true);
    try {
      // Update conversation with latest proposal details
      // IMPORTANT: Reset both confirmation flags when a new price is proposed
      const { error: updateError } = await supabase
        .from('conversations')
        .update({
          latest_proposal_price: price,
          latest_proposal_pricing_type: 'fixed',
          latest_proposal_by: user!.id,
          price_confirmed_by_provider: false,
          price_agreed_by_non_posting_party: false,
          final_price: null
        })
        .eq('id', conversation.id);

      if (updateError) throw updateError;

      // Send a system message about the price proposal
      const { error } = await supabase
        .from('messages')
        .insert({
          conversation_id: conversation.id,
          sender_id: user!.id,
          text: `[PRICE_PROPOSAL] Proposed price: $${price}`
        });

      if (error) throw error;

      setIsProposePrice(false);
      setProposedPrice("");
      toast.success("Price proposal sent");
      onUpdate();
    } catch (error) {
      console.error('Error sending price proposal:', error);
      toast.error("Failed to send price proposal");
    } finally {
      setIsSubmitting(false);
    }
  };

  const acceptPrice = async (price: number) => {
    if (!conversation) return;

    setIsSubmitting(true);
    try {
      // Record who is accepting the price so the client can pay as soon as the provider accepts.
      const updateData: {
        final_price: number;
        price_confirmed_by_provider?: boolean;
        price_agreed_by_non_posting_party?: boolean;
      } = { final_price: price };
      if (isCurrentUserProvider) {
        updateData.price_confirmed_by_provider = true;
      } else if (isCurrentUserClient) {
        updateData.price_agreed_by_non_posting_party = true;
      }

      const { error } = await supabase
        .from('conversations')
        .update(updateData)
        .eq('id', conversation.id);

      if (error) throw error;

      // Send confirmation message
      await supabase
        .from('messages')
        .insert({
          conversation_id: conversation.id,
          sender_id: user!.id,
          text: `[PRICE_ACCEPTED] Agreed to price: $${price}`
        });

      // Trigger a manual scroll check since this is a system message
      setTimeout(() => {
        const messagesContainer = document.querySelector('[data-messages-container]');
        if (messagesContainer) {
          messagesContainer.scrollTop = messagesContainer.scrollHeight;
        }
      }, 200);

      toast.success("Price accepted");
      onUpdate();
    } catch (error) {
      console.error('Error accepting price:', error);
      toast.error("Failed to accept price");
    } finally {
      setIsSubmitting(false);
    }
  };

  const acceptOriginalPrice = async () => {
    if (!conversation) return;

    setIsSubmitting(true);
    try {
      // Provider confirmation should immediately unlock Pay for the client.
      const isProviderConfirming = isCurrentUserProvider;

      const updateData: {
        price_confirmed_by_provider?: boolean;
        price_agreed_by_non_posting_party?: boolean;
      } = {};
      if (isProviderConfirming) {
        updateData.price_confirmed_by_provider = true;
      } else {
        updateData.price_agreed_by_non_posting_party = true;
      }

      const { error } = await supabase
        .from('conversations')
        .update(updateData)
        .eq('id', conversation.id);

      if (error) throw error;

      // Send confirmation message
      const messageText = isProviderConfirming
        ? `[PRICE_CONFIRMED] Provider confirmed price: $${getCurrentPrice()}`
        : `[PRICE_ACCEPTED] Agreed to price: $${getCurrentPrice()}`;
        
      await supabase
        .from('messages')
        .insert({
          conversation_id: conversation.id,
          sender_id: user!.id,
          text: messageText
        });
        
      // Trigger a manual scroll check since this is a system message
      setTimeout(() => {
        const messagesContainer = document.querySelector('[data-messages-container]');
        if (messagesContainer) {
          messagesContainer.scrollTop = messagesContainer.scrollHeight;
        }
      }, 200);

      toast.success(isProviderConfirming ? "Price confirmed" : "Original price accepted");
      onUpdate();
    } catch (error) {
      console.error('Error accepting original price:', error);
      toast.error("Failed to accept original price");
    } finally {
      setIsSubmitting(false);
    }
  };

  const markAsInProgress = async () => {
    if (!conversation) return;
    
    setIsSubmitting(true);
    try {
      const { error } = await supabase
        .from('conversations')
        .update({ service_started: true })
        .eq('id', conversation.id);

      if (error) throw error;

      // Send system message about service starting
      await supabase
        .from('messages')
        .insert({
          conversation_id: conversation.id,
          sender_id: user!.id,
          text: '[SYSTEM] Service has been started by the provider'
        });

      toast.success("Service marked as in progress");
      onUpdate();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error("Failed to update status");
    } finally {
      setIsSubmitting(false);
    }
  };

  const markAsCompleted = async () => {
    if (!conversation) return;
    
    setIsSubmitting(true);
    try {
      const { error } = await supabase
        .from('conversations')
        .update({ service_completed: true })
        .eq('id', conversation.id);

      if (error) throw error;

      // Send system message about service completion
      await supabase
        .from('messages')
        .insert({
          conversation_id: conversation.id,
          sender_id: user!.id,
          text: '[SYSTEM] Service has been marked as completed by the client'
        });

      toast.success("Service marked as completed");
      onUpdate();
      
      // Trigger review modal for client
      if (isClient && onServiceCompleted) {
        onServiceCompleted();
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error("Failed to update status");
    } finally {
      setIsSubmitting(false);
    }
  };

  const status = getStatus();

  // Determine if provider has agreed to the current price
  // Provider has agreed if:
  // 1. They confirmed the price explicitly (price_confirmed_by_provider = true), OR
  // 2. They were the one who proposed the latest price (latest_proposal_by === provider's ID)
  const providerHasAgreedToCurrentPrice = () => {
    if (!conversation) return false;

    // If payment already completed, no need to show pay button
    if (conversation.payment_completed) return false;

    // If provider explicitly confirmed the price
    if (conversation.price_confirmed_by_provider) return true;

    // If provider proposed the latest price, they implicitly agree to it
    if (conversation.latest_proposal_by && conversation.latest_proposal_by === conversation.participant2_id) {
      return true;
    }

    // If a final price exists, treat it as agreed (covers any older flows)
    if (conversation.final_price != null) return true;

    return false;
  };

  // Should show confirm button for provider?
  // Only if they haven't confirmed yet AND they didn't propose the latest price
  const shouldShowProviderConfirmButton = () => {
    if (!conversation) return false;
    if (status !== 'proposal') return false;
    if (!isCurrentUserProvider) return false;
    if (conversation.payment_completed) return false;
    if (conversation.price_confirmed_by_provider) return false;

    // If provider proposed the latest price, they've already implicitly agreed
    if (conversation.latest_proposal_by && conversation.latest_proposal_by === conversation.participant2_id) {
      return false;
    }

    return true;
  };

  // Should show pay button for client?
  const shouldShowClientPayButton = () => {
    if (!conversation) return false;
    if (status !== 'proposal') return false;
    if (!isCurrentUserClient) return false;
    if (conversation.payment_completed) return false;

    return providerHasAgreedToCurrentPrice();
  };

  return (
    <div className="flex items-center justify-between w-full">
      <div className="flex items-center gap-3">
        <UserAvatar
          userId={recipient.id}
          avatarUrl={recipient.avatarUrl}
          fallback={recipient.avatarInitials}
          size="lg"
        />
        
        <div>
          <div className="flex items-center gap-2">
            <h1 className="font-poppins font-bold text-xl text-foreground">{recipient.name}</h1>
            {status && getStatusBadge(status)}
          </div>
          
          <div className="flex items-center gap-4 text-sm">
            <UserStatusIndicator userId={recipient.id} />
            
            {conversation?.services?.title && (
              <span className="text-muted-foreground">
                Service: {conversation.services.title}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Right side - Service controls and pricing - Always show if conversation exists */}
      {conversation && (
        <div className="flex items-center gap-3">
          {/* Price display - Always visible */}
          <div className="flex items-center gap-2">
            <DollarSign className="h-4 w-4 text-muted-foreground" />
            <span className="font-medium">
              ${getCurrentPrice()}{getPricingTypeDisplay()}
            </span>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Provider Confirm Price Button */}
            {shouldShowProviderConfirmButton() && (
              <Button
                size="sm"
                onClick={acceptOriginalPrice}
                disabled={isSubmitting}
                className="h-7 px-3 text-xs bg-blue-600 hover:bg-blue-700 text-white"
              >
                <Check className="h-3 w-3 mr-1" />
                Confirm ${getCurrentPrice()}
              </Button>
            )}

            {/* Client Pay Button */}
            {shouldShowClientPayButton() && (
              <Button
                size="sm"
                onClick={() => setShowPaymentModal(true)}
                className="h-7 px-3 text-xs bg-green-600 hover:bg-green-700 text-white"
              >
                <CreditCard className="h-3 w-3 mr-1" />
                Pay ${getCurrentPrice()}
              </Button>
            )}

            {/* Adjust Price Button - Available to both parties, hide if payment is completed */}
            {!conversation.payment_completed && !isProposePrice && (
              <Button
                size="sm"
                onClick={() => setIsProposePrice(true)}
                className="h-7 px-3 text-xs bg-orange-100 border border-orange-300 text-orange-700 hover:bg-orange-200"
              >
                Adjust Price
              </Button>
            )}

            {/* Mark Complete Button - For CLIENT after payment is completed, not yet completed */}
            {!conversation.service_completed && conversation.payment_completed && isClient && (
              <Button
                size="sm"
                onClick={markAsCompleted}
                disabled={isSubmitting}
                className="h-7 px-3 text-xs bg-green-600 hover:bg-green-700 text-white"
              >
                <CheckCircle className="h-3 w-3 mr-1" />
                Mark Complete
              </Button>
            )}
          </div>

          {/* Price Proposal Input */}
          {isProposePrice && (
            <div className="flex items-center gap-2">
              <Input
                type="number"
                value={proposedPrice}
                onChange={(e) => setProposedPrice(e.target.value)}
                placeholder="Adjust Price"
                className="h-7 w-32 text-sm"
                min="0"
                step="0.01"
              />
              <Button
                size="sm"
                onClick={sendPriceProposal}
                disabled={isSubmitting}
                className="h-7 px-2"
              >
                <Send className="h-3 w-3" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setIsProposePrice(false)}
                className="h-7 px-2"
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Payment Modal */}
      {conversation && (
        <Suspense fallback={null}>
          <PaymentModal
            isOpen={showPaymentModal}
            onClose={() => setShowPaymentModal(false)}
            providerId={recipient.id}
            providerName={recipient.name}
            amount={getCurrentPrice()}
            conversationId={conversation.id}
            serviceId={conversation.service_id || undefined}
            onPaymentSuccess={onUpdate}
          />
        </Suspense>
      )}
    </div>
  );
}